import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

// public
import HomePage from '@/public/HomePage.vue'
import SyncAppPage from '@/public/SyncAppPage.vue'
import PrivacyPage from '@/public/PrivacyPage.vue'
import TermsPage from '@/public/TermsPage.vue'
import PlansPage from '@/public/PlansPage.vue'
import FamilyLandingPage from '@/public/FamilyLandingPage.vue'
import CreateTreePage from '@/public/CreateTreePage.vue'
import ShareTreePage from '@/public/ShareTreePage.vue'
import DeveloperLandingPage from '@/public/DeveloperLandingPage.vue'

import SignUpPage from '@/auth/SignUpPage.vue'
import SignUpInvitedPage from '@/auth/SignUpInvitedPage.vue'
import SignUpDonePage from '@/auth/SignUpDonePage.vue'
import SignInPage from '@/auth/SignInPage.vue'
import SignInCodePage from '@/auth/SignInCodePage.vue'
import TestSignInPage from '@/auth/TestSignInPage.vue'
import DeviceCodePage from '@/auth/DeviceCodePage.vue'
import AuthorizePage from '@/auth/AuthorizePage.vue'
import AuthRedirectPage from '@/auth/AuthRedirectPage.vue'
import SignOutPage from '@/auth/SignOutPage.vue'

import SupportPage from '@/support/SupportPage.vue'
import FAQPage from '@/learn/FAQPage.vue'
import ContactPage from '@/support/ContactPage.vue'

import SyncOverviewPage from '@/learn/SyncOverviewPage.vue'
import FamilyGroupOverviewPage from '@/learn/FamilyGroupOverviewPage.vue'
import ResearchPage from '@/learn/ResearchPage.vue'
import SupervisedUsersPage from '@/learn/SupervisedUsersPage.vue'
import ComparePage from '@/learn/ComparePage.vue'

// manage
import AccountPage from '@/account/AccountPage.vue'
import FamilyGroupListPage from '@/manage/familygroups/FamilyGroupListPage.vue'
import FamilyGroupPage from '@/manage/familygroups/FamilyGroupPage.vue'
import FamilyGroupInviteAcceptPage from '@/manage/familygroups/FamilyGroupInviteAcceptPage.vue'
import ProfileManagePage from '@/manage/profiles/ProfileManagePage.vue'
import ProfilePage from '@/manage/profiles/ProfilePage.vue'
import ProfileMatchesPage from '@/manage/profiles/ProfileMatchesPage.vue'
import ResearchGroupListPage from '@/manage/researchgroups/ResearchGroupListPage.vue'
import ResearchGroupPage from '@/manage/researchgroups/ResearchGroupPage.vue'
import UserProfilePage from '@/manage/users/UserProfilePage.vue'

// explore
import UserHomePage from '@/explore/UserHomePage.vue'
import SyncSessionPage from '@/sync/SyncSessionPage.vue'
import { default as SyncFlowAppPage } from '@/sync/SyncAppPage.vue'
import PersonPage from '@/explore/PersonPage.vue'
import TopNamesPage from '@/explore/names/TopNamesPage.vue'
import NameSearchPage from '@/explore/names/NameSearchPage.vue'
import PlacesPage from '@/explore/places/PlacesPage.vue'
import GamesPage from '@/explore/games/GamesPage.vue'
import MatchingGame from '@/games/matching/MatchingGame.vue'
import GameBuilderPage from '@/games/GameBuilderPage.vue'
import NewGamePage from '@/games/NewGamePage.vue'
import NewsPage from '@/explore/news/NewsPage.vue'
import ExploreTimePage from '@/explore/time/ExploreTimePage.vue'
import HighlightsPage from '@/explore/highlights/HighlightsPage.vue'

// admin
import AdminHomePage from '@/admin/AdminHomePage.vue'
import ManageUsersPage from '@/admin/ManageUsersPage.vue'
import ManageIdentitiesPage from '@/admin/ManageIdentitiesPage.vue'

const routes: Array<RouteRecordRaw> = [
  // public
  { path: '/', component: HomePage },
  { path: '/syncapp', name: 'syncapp', component: SyncAppPage },
  { path: '/privacy', component: PrivacyPage },
  { path: '/terms', component: TermsPage },
  { path: '/start/family', component: FamilyLandingPage },
  { path: '/start/new-tree', component: CreateTreePage },
  { path: '/share', component: ShareTreePage },
  { path: '/start/dev', component: DeveloperLandingPage },
  { path: '/signup', component: SignUpPage },
  { path: '/signup/invited', component: SignUpInvitedPage },
  { path: '/signup/done', component: SignUpDonePage },
  { path: '/signin', component: SignInPage },
  { path: '/signin/code', component: SignInCodePage },
  { path: '/testsignin', component: TestSignInPage },
  { path: '/signin/approve', component: DeviceCodePage },
  { path: '/authorize', component: AuthorizePage },
  { path: '/authredirect', component: AuthRedirectPage },
  { path: '/signout', component: SignOutPage },
  { path: '/about', name: "about",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "about" */ '@/public/AboutPage.vue')
  },
  { path: '/plans', component: PlansPage },
  { path: '/learn/sync', name: 'sync', component: SyncOverviewPage },
  { path: '/learn/groups', component: FamilyGroupOverviewPage },
  { path: '/learn/faq', name: 'faq', component: FAQPage },
  { path: '/learn/research', name: 'research', component: ResearchPage },
  { path: '/learn/supervised-users', name: 'supervisedusers', component: SupervisedUsersPage },
  { path: '/learn/compare', name: 'compare', component: ComparePage },
  { path: '/support', name: 'support', component: SupportPage },
  { path: '/contact', name: 'contact', component: ContactPage },

  // manage
  { path: '/account', name: 'account', component: AccountPage },
  { path: '/users/:userId/account', component: AccountPage, props: true },
  { path: '/account', name: 'account.supervisedTab', component: AccountPage },
  { path: '/account', name: 'account.familyGroupsTab', component: AccountPage },
  { path: '/groups', component: FamilyGroupListPage },
  { path: '/groups/:familyGroupId', name: 'familyGroup', component: FamilyGroupPage, props: true },
  { path: '/groups/:familyGroupId', name: 'familyGroup.researchGroupsTab', component: FamilyGroupPage, props: true },
  { path: '/groupinvites/:id/accept', 
      component: FamilyGroupInviteAcceptPage, 
      props: route => ({ inviteId: route.params.id, inviteCode: route.query.code }),
      meta: { redact: ['code'] }},
  { path: '/profile', component: ProfilePage, props: true }, // set props to undefined
  { path: '/profiles', component: ProfileManagePage },
  { path: '/profiles/:personId', component: ProfilePage, props: true },
  { path: '/profiles/:personId/matches', component: ProfileMatchesPage, props: true },
  { path: '/trees', component: ResearchGroupListPage },
  { path: '/trees/new', component: SyncSessionPage },
  { path: '/trees/:dataGroupId', name: 'researchGroup', component: ResearchGroupPage, props: true },
  { path: '/trees/:dataGroupId', name: 'researchGroup.manageTab', component: ResearchGroupPage, props: true },
  { path: '/users/:userId', component: UserProfilePage, props: true },
  { path: '/users/:userId/profiles', component: ProfileManagePage, props: true },

  // explore
  { path: '/home', component: UserHomePage },
  { path: '/sync/new/syncapp', component: SyncFlowAppPage },
  { path: '/persons/:personId', component: PersonPage, props: true  },
  { path: '/names', component: TopNamesPage },
  { path: '/names/:part/:name', name: 'namesearch', component: NameSearchPage, props: true},
  { path: '/places', component: PlacesPage },
  { path: '/games', component: GamesPage },
  { path: '/games/new', component: NewGamePage },
  { path: '/games/:gameId/design', component: GameBuilderPage, props: true },
  { path: '/games/:gameId?', component: MatchingGame, props: true },
  { path: '/news', component: NewsPage },
  { path: '/time', component: ExploreTimePage },
  { path: '/highlights', component: HighlightsPage },

  // admin
  { path: '/admin', component: AdminHomePage },
  { path: '/admin/users', component: ManageUsersPage },
  { path: '/admin/identities', component: ManageIdentitiesPage },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // If the route has a hash (anchor link), scroll to the element
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    }
    
    // If the route has savedPosition, use it for smooth scrolling
    if (savedPosition) {
      return savedPosition;
    }

    // Default behavior: scroll to the top of the page
    return { top: 0, behavior: 'smooth' };
  }
})

router.beforeEach((to, from, next) => {
  if (typeof to.name == 'string' && to.name.includes('.')) {
    const parts = to.name.split('.', 2)
    to.meta.initialTab = parts[1]
  }
  next()
})

export default router
